import type { MeasurementName } from '@quromedical/models';

import type { Calculate, Configuration, Measurement, Vitals } from './internal';

export type GetClosesVitals = (
  relative: number,
  vitals: Measurement[],
  window?: number,
) => Vitals;

/**
 * make the value to be always a positive and subtract ts from the relative value
 */
const getOffset = (ts: number, relative: number): number =>
  Math.abs(relative - ts);

/**
 * get the closet value by comparing the new value from the exiting value
 */
const getClosest = (
  relative: number,
  newValue: Measurement,
  existingValue?: Measurement,
): Measurement => {
  if (!existingValue) {
    return newValue;
  }

  const aOffset = getOffset(newValue.ts, relative);
  const bOffset = getOffset(existingValue.ts, relative);

  return aOffset < bOffset ? newValue : existingValue;
};

const getWindow = (relative: number, windowSize: number): readonly number[] =>
  [relative - windowSize, relative + windowSize] as const;

type IsInWindow = (ts: number) => boolean;

/**
 * Create a function that will return whether or not a given timeframe is within the provided window
 *
 * @param relative midpoint of the window, times compared relative to this point
 * @param windowSize if provided as zero will return true for times that are equal to `relative`
 */
const createWindowCheck = (
  relative: number,
  windowSize: number,
): IsInWindow => {
  const [start, end] = getWindow(relative, windowSize);

  return (ts: number) => start <= ts && end >= ts;
};

/**
 * Get one of each vital closes to the given relative time withing the appropriate window size
 */
export const getClosestVitals: GetClosesVitals = (
  relative,
  vitals,
  window = 0,
) => {
  const isInWindow = createWindowCheck(relative, window);

  const closeVital = vitals.reduce<Vitals>((prev, curr) => {
    const inWindow = isInWindow(curr.ts);

    if (!inWindow) {
      return prev;
    }

    const type = curr.type;
    return {
      ...prev,
      [type]: getClosest(relative, curr, prev[type]),
    };
  }, {});

  return closeVital;
};

const getBaseMeasures = (base: MeasurementName, input: Measurement[]) =>
  input.filter((metric) => metric.type === base);

const resolveMeasures = <TParams>(
  config: Configuration<TParams>,
  input: Measurement[],
  params: TParams,
) => {
  const baseMeasures = getBaseMeasures(config.base, input);

  return baseMeasures
    .map((relative) => {
      const closestVitals = getClosestVitals(relative.ts, input, config.window);

      return config.resolve(relative.ts, closestVitals, params);
    })
    .flat();
};

/**
 * allows us to create a calculator from a set of interpreter configurations so that the user does not manually need to traverse a collection of configs
 */
export const interpret =
  <TParams>(configs: Configuration<TParams>[]): Calculate<TParams> =>
  (input, params) =>
    configs.map((config) => resolveMeasures(config, input, params)).flat();

/**
 * compose allows us to merge multiple calculators into a single calculator that we can apply without the need for many function calls. this enables separation of calculators which makes them easier to manage, define, and test
 */
export const compose =
  <TParams>(resolvers: Calculate<TParams>[]): Calculate<TParams> =>
  (input, params) =>
    resolvers.map((calculate) => calculate(input, params)).flat();
